import { CALCULATED_FEE, CASH_PRODUCT_TYPE } from '@repo/constants';
import { FormValidationMessage } from '@repo/constants';
import { SecuritySubType } from '@repo/types';
import { z } from 'zod';

export const SingleTradeSchema = z
  .object({
    clientAccount: z
      .number({
        required_error: FormValidationMessage.Required,
        invalid_type_error: FormValidationMessage.Required,
      })
      .min(1, FormValidationMessage.Required),
    trader: z.number().optional().nullable(),
    tradeDate: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    settlementDate: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    securityType: z
      .number({
        required_error: FormValidationMessage.Required,
        invalid_type_error: FormValidationMessage.Required,
      })
      .min(1, FormValidationMessage.Required),
    securitySubType: z.number().optional().nullable(),
    securityId: z.number().optional().nullable(),
    cashCurrency: z.number().optional().nullable(),
    market: z
      .number({
        required_error: FormValidationMessage.Required,
        invalid_type_error: FormValidationMessage.Required,
      })
      .min(1, FormValidationMessage.Required),
    multiplier: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    side: z
      .number({
        required_error: FormValidationMessage.Required,
        invalid_type_error: FormValidationMessage.Required,
      })
      .min(1, FormValidationMessage.Required),
    broker: z
      .number({
        required_error: FormValidationMessage.Required,
        invalid_type_error: FormValidationMessage.Required,
      })
      .min(1, FormValidationMessage.Required),
    quantity: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    grossPrice: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    conversionRate: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    tradeCcy: z
      .number({
        required_error: FormValidationMessage.Required,
        invalid_type_error: FormValidationMessage.Required,
      })
      .min(1, FormValidationMessage.Required),
    settlementCcy: z
      .number({
        required_error: FormValidationMessage.Required,
        invalid_type_error: FormValidationMessage.Required,
      })
      .min(1, FormValidationMessage.Required),
    commissionType: z.number().nullable().optional(),
    commissionRate: z.string().nullable().optional(),
    commissionAmount: z.string().nullable().optional(),
    couponAccrual: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    linkedTrade: z.string().optional().nullable(),
    excludePL: z.boolean().optional().nullable(),
    processedTrade: z.boolean().optional().nullable(),
    fop: z.boolean().optional().nullable(),
    settlementAmount: z
      .string({
        required_error: FormValidationMessage.Required,
        invalid_type_error: FormValidationMessage.Required,
      })
      .max(20, FormValidationMessage.Max(20)),
    tradeAmount: z
      .string({
        required_error: FormValidationMessage.Required,
        invalid_type_error: FormValidationMessage.Required,
      })
      .max(20, FormValidationMessage.Max(20)),
    grossAmount: z
      .string({
        required_error: FormValidationMessage.Required,
        invalid_type_error: FormValidationMessage.Required,
      })
      .max(20, FormValidationMessage.Max(20)),
    taxTradeFee: z.string({
      required_error: FormValidationMessage.Required,
      invalid_type_error: FormValidationMessage.Required,
    }),
    feeId: z.number().optional().nullable(),
    tradeType: z.number().optional().nullable(),
    borrowRate: z.string().optional().nullable(),
    tradeStrategy: z.number().optional().nullable(),
    executionDate: z.string().optional().nullable(),
  })
  .superRefine((data, ctx) => {
    // Security Sub Type is required for cash product type
    if (data.securityType === CASH_PRODUCT_TYPE && !data.securitySubType) {
      ctx.addIssue({
        path: ['securitySubType'],
        code: z.ZodIssueCode.custom,
        message: FormValidationMessage.Required,
      });
    }
    // Cash Currency is required for cash product type
    if (data.securityType === CASH_PRODUCT_TYPE && !data.cashCurrency) {
      ctx.addIssue({
        path: ['cashCurrency'],
        code: z.ZodIssueCode.custom,
        message: FormValidationMessage.Required,
      });
    }
    // Security Id is required for non-cash product type
    if (data.securityType !== CASH_PRODUCT_TYPE && !data.securityId) {
      ctx.addIssue({
        path: ['securityId'],
        code: z.ZodIssueCode.custom,
        message: FormValidationMessage.Required,
      });
    }
    // Fee Id is required for cash product type with calculated fee security sub type
    if (
      data.securityType === CASH_PRODUCT_TYPE &&
      data.securitySubType === CALCULATED_FEE &&
      !data.feeId
    ) {
      ctx.addIssue({
        path: ['feeId'],
        code: z.ZodIssueCode.custom,
        message: FormValidationMessage.Required,
      });
    }
    // Conversion Rate is required if trade ccy is not the same as settlement ccy
    if (data.tradeCcy !== data.settlementCcy && !data.conversionRate) {
      ctx.addIssue({
        path: ['conversionRate'],
        code: z.ZodIssueCode.custom,
        message: FormValidationMessage.Required,
      });
    }

    // Report Date is required for cash product type with forward fx contract security sub type
    if (
      data.securityType === CASH_PRODUCT_TYPE &&
      data.securitySubType === SecuritySubType.ForwardFXContract &&
      !data.executionDate
    ) {
      ctx.addIssue({
        path: ['executionDate'],
        code: z.ZodIssueCode.custom,
        message: FormValidationMessage.Required,
      });
    }
  });

export type SingleTradeSchema = z.infer<typeof SingleTradeSchema>;
